import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import QRCode from "qrcode.react"; // For React UI
import * as QRCodeLib from "qrcode"; // For PDF helpers
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  ArrowDownTrayIcon,
  CheckIcon,
  PrinterIcon,
} from "@heroicons/react/20/solid";
import { axiosInstance } from "../../utils/axios";
import html2pdf from "html-to-pdf-js";
import { useReactToPrint } from "react-to-print";
import Ritex from "../../assets/Ritex.png";
import AdminSidebar from "../../components/AdminSidebar";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

// Helper to generate QR code data URL
async function generateQRCodeDataUrl(text) {
  return await new Promise((resolve) => {
    QRCodeLib.toDataURL(text, { margin: 1, width: 200 }, (err, url) => {
      resolve(url);
    });
  });
}

// Helper to convert image URL to data URL (for local images)
async function getImageDataUrl(url) {
  // For remote images, just return the URL
  if (/^https?:\/\//.test(url)) return url;
  // For local images, fetch and convert to data URL
  const response = await fetch(url);
  const blob = await response.blob();
  return await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

// PDF Styles (update for your sample)
const pdfStyles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#fff",
    padding: 0,
  },
  blueBar: {
    height: 12,
    backgroundColor: "#1460d9", // Blue primary
    width: "100%",
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 40,
    marginHorizontal: 40,
  },
  brandLogo: {
    width: 80,
    height: 80,
    objectFit: "contain",
  },
  modelText: {
    fontSize: 28,
    fontWeight: "bold",
    textAlign: "right",
    marginRight: 12,
  },
  uniqueId: {
    fontSize: 64,
    fontWeight: "semibold",
    color: "#22c55e", // Green
    textAlign: "center",
    marginTop: 20,
  },
  serialNo: {
    fontSize: 64,
    fontWeight: "semibold",
    color: "#ff1c46", // Light red
    textAlign: "center",
    marginTop: 4,
  },
  qrCode: {
    width: 100,
    height: 100,
    alignSelf: "center",
  },
  detailsBox: {
    border: "1px solid #222",
    borderLeft: "6px solid #1460d9", // Blue primary
    marginVertical: 16,
    marginHorizontal: 40,
    padding: 16,
    minHeight: 160,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#ff1c46", // Light red
    marginRight: 4,
  },
  detailValue: {
    fontSize: 12,
    color: "#222",
  },
  detailValueRed: {
    fontSize: 12,
    color: "#ff1c46", // Light red
    fontWeight: "bold",
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 10,
    gap: 16,
  },
  footerLogo: {
    width: 80,
    height: 80,
    objectFit: "contain",
    marginRight: 12,
  },
  footerText: {
    fontSize: 18,
    color: "#222",
    marginLeft: 8,
  },
});

// PDF Document Component
const DevicePDFDocument = ({ pdfDevices }) => (
  <Document>
    {pdfDevices.map((device, index) => (
      <Page key={index} size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.blueBar} />
        <View style={pdfStyles.headerRow}>
          {device.brandLogoUrl && (
            <Image style={pdfStyles.brandLogo} src={device.brandLogoUrl} />
          )}
          <Text style={pdfStyles.modelText}>{device.model || "N/A"}</Text>
        </View>
        <Text style={pdfStyles.uniqueId}>{device.unique_id || "N/A"}</Text>
        <Text style={pdfStyles.serialNo}>{device.serial_no || "N/A"}</Text>
        {device.qrCodeUrl && (
          <Image style={pdfStyles.qrCode} src={device.qrCodeUrl} />
        )}
        <View style={pdfStyles.detailsBox}>
          {device.counter_color > 0 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailLabel}>COUNTER COLOR:</Text>
              <Text style={pdfStyles.detailValueRed}>
                {device.counter_color || "N/A"}
              </Text>
            </View>
          )}
          {device.counter_black > 0 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailValue}>
                COUNTER BLACK: {device.counter_black || "N/A"}
              </Text>
            </View>
          )}
          {device.total_counter > 0 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailValue}>
                TOTAL COUNTER: {device.total_counter || "N/A"}
              </Text>
            </View>
          )}
          {device.device_options?.finisher === 1 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailValue}>FINISHER: ✓</Text>
            </View>
          )}
          {device.device_options?.["rt-lct"] === 1 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailValue}>RT/LCT: ✓</Text>
            </View>
          )}

          {device.device_options?.["no-df"] === 1 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailValue}>NO DF: ✓</Text>
            </View>
          )}
          <View style={pdfStyles.detailRow}>
            <Text style={pdfStyles.detailValue}>
              TRAYS: {device.device_options?.trays || "N/A"}
            </Text>
          </View>
          {device.device_options?.spare_parts > 0 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailValue}>
                SPARE PARTS: {device.device_options.spare_parts}
              </Text>
            </View>
          )}
          {device.quantity > 0 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailLabel}>QUANTITY:</Text>
              <Text style={pdfStyles.detailValueRed}>{device.quantity}</Text>
            </View>
          )}
          {device.stock > 0 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailLabel}>Stock:</Text>
              <Text style={pdfStyles.detailValueRed}>{device.stock}</Text>
            </View>
          )}
          {device.palette > 0 && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailLabel}>PALETTE:</Text>
              <Text style={pdfStyles.detailValueRed}>{device.palette}</Text>
            </View>
          )}
          {device.comment && (
            <View style={pdfStyles.detailRow}>
              <Text style={pdfStyles.detailValue}>
                COMMENT: {device.comment}
              </Text>
            </View>
          )}
          <View style={pdfStyles.detailRow}>
            <Text style={pdfStyles.detailValue}>
              Last edited by:{" "}
              {device.user
                ? `${device.user.name?.[0] || ""}.${
                    device.user.last_name?.[0] || ""
                  }`
                : "N/A"}
            </Text>
          </View>
        </View>
        <View style={pdfStyles.footer}>
          {device.footerLogoUrl && (
            <Image style={pdfStyles.footerLogo} src={device.footerLogoUrl} />
          )}
          <Text style={pdfStyles.footerText}>www.aritex.ch</Text>
        </View>
      </Page>
    ))}
  </Document>
);

// Helper to prepare PDF device data
async function preparePdfDevices(devices) {
  const footerLogoUrl = await getImageDataUrl(Ritex);
  return await Promise.all(
    devices.map(async (device) => {
      const brandLogoUrl = device.brand_logo
        ? window.REACT_APP_API_ENDPOINT.replace(/\/api(?!.*\/api)/, "") +
          device.brand_logo
        : null;
      const qrCodeUrl = await generateQRCodeDataUrl(device.unique_id);
      return {
        ...device,
        trays: device.device_options?.trays || "N/A",
        register_by: device.user
          ? `${device.user.name?.[0] || ""}.${device.user.last_name?.[0] || ""}`
          : "N/A",
        brandLogoUrl,
        qrCodeUrl,
        footerLogoUrl,
      };
    })
  );
}

function MultiDeviceDetails() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);

  // Get parameters from URL
  const deviceIds =
    searchParams
      .get("ids")
      ?.split(",")
      .filter((id) => id.trim()) || [];
  const printOnLoad = searchParams.get("printOnLoad") === "true";

  const componentRefs = useRef({});

  // Convert string IDs to numbers and filter out invalid ones
  const numericIds = deviceIds
    .map((id) => parseInt(id.trim()))
    .filter((id) => !isNaN(id));

  // React Query for fetching devices
  const {
    data: devicesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["devices", numericIds],
    queryFn: async () => {
      if (numericIds.length === 0) {
        throw new Error("No valid device IDs provided");
      }

      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/devices/multiple`,
        {
          ids: numericIds,
        }
      );

      if (response.data && response.data.devices) {
        return {
          devices: response.data.devices,
          nextUniqueId: response.data.next_unique_id,
        };
      } else {
        throw new Error("No devices found");
      }
    },
    enabled: numericIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  const devices = devicesData?.devices || [];
  const nextUniqueId = devicesData?.nextUniqueId;

  useEffect(() => {
    if (printOnLoad && devices.length > 0) {
      handlePrintSingle(activeTab);
    }
  }, [printOnLoad, devices.length, activeTab]);

  const handlePrintSingle = useReactToPrint({
    content: () => componentRefs.current[activeTab],
    pageStyle: `
      @page {
        size: A4;
        min-width: 100%;
        width: 100%;
      }
      body {
        min-width: 100%;
        width: 100%;
        padding: 0;
        margin: 0;
      }
      .device-details{
        overflow: hidden;
        box-sizing: border-box;
        min-width: 100%;
        width: 100%;
      }
      .print-bottom {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        background-color: white;
      }
    `,
  });

  const handlePrintAll = useReactToPrint({
    content: () => componentRefs.current["printAll"],
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      body {
        margin: 0;
        padding: 0;
      }
      .device-details {
        page-break-after: always;
      }
    `,
  });

  const handleDownloadSingle = async () => {
    const element = componentRefs.current[activeTab];
    if (!element) return;

    const opt = {
      margin: 1,
      filename: `device-${devices[activeTab]?.unique_id || activeTab}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  const handleDownloadAll = async () => {
    try {
      const pdfDevices = await preparePdfDevices(devices);
      console.log(pdfDevices);
      const blob = await pdf(
        <DevicePDFDocument pdfDevices={pdfDevices} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `device-report-${new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  const DeviceCard = ({ device, index, isActive = false }) => {
    if (device.error) {
      return (
        <div className="md:max-w-[620px] w-full bg-white border-t-red-500 border-t-[20px] py-6 px-6">
          <div className="text-center text-red-500">
            <p>Failed to load device {device.id}</p>
            <p className="text-sm">{device.error}</p>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={(el) => (componentRefs.current[index] = el)}
        className={`device-details md:max-w-[620px] w-full bg-white border-t-bluePrimary border-t-[20px] py-6 px-6 overflow-scroll lg:overflow-hidden ${
          !isActive ? "hidden" : ""
        }`}
      >
        <div className="flex items-center mb-4 space-y-4 md:space-y-0 md:space-x-4">
          {device.brand_logo && (
            <div className="w-1/4 md:mb-0">
              <img
                src={
                  window.REACT_APP_API_ENDPOINT.replace(
                    /\/api(?!.*\/api)/,
                    ""
                  ) + device.brand_logo
                }
                alt="brand-logo"
                className="w-full"
              />
            </div>
          )}

          {!device.brand_logo && (
            <div className="!mt-0 w-1/4">
              <h3 className="uppercase font-bold text-4xl text-center">
                {device.brand_name}
              </h3>
            </div>
          )}
          <div className="w-3/4">
            <h3 className="!mt-0 uppercase font-bold text-4xl text-right">
              {device.model}
            </h3>
          </div>
        </div>
        <h4 className="text-center text-green-500 font-bold text-7xl">
          {device.unique_id}
        </h4>
        <div className="text-center mb-8">
          <h3 className="uppercase text-lightRed font-bold text-7xl">
            {device.serial_no?.replace(/\s+/g, "") ?? ""}
          </h3>
        </div>
        <div className="flex justify-center mb-8">
          <QRCode value={device.unique_id} className="device-qr-code" />
        </div>
        <div className="border border-black border-l-bluePrimary border-l-8 p-4 flex flex-col gap-2">
          {/* Counters */}
          <div className="mb-2 flex flex-col gap-1">
            {["counter_color", "counter_black", "total_counter"].map((key) => (
              <p
                key={key}
                className={`uppercase text-sm md:text-md mb-0 ${
                  device[key] > 0 ? "" : "opacity-50"
                }`}
              >
                {key.replace("_", " ")}: {device[key] ?? "N/A"}
              </p>
            ))}
          </div>
          {device.device_options && (
            <div className="flex flex-col gap-1 mb-2">
              <p className="uppercase text-sm md:text-md flex items-center gap-2">
                Options:{" "}
                {[
                  { key: "finisher", label: "finisher" },
                  { key: "rt_lct", label: "rt/lct" },
                  { key: "cabinet", label: "cabinet" },
                  { key: "no_df", label: "no df" },
                  { key: "green", label: "green" },
                  { key: "blue", label: "blue" },
                ]
                  .filter(
                    (opt) =>
                      // Accept both snake_case and kebab-case for compatibility
                      device.device_options[opt.key] === 1 ||
                      device.device_options[opt.key.replace("_", "-")] === 1
                  )
                  .map((opt) => opt.label)
                  .join(", ") || "-"}
              </p>
            </div>
          )}
          {/* Trays */}
          <div className="mb-2">
            <p className="uppercase text-sm md:text-md flex">
              trays: {device.device_options?.trays}
            </p>
          </div>
          {device.stock > 0 && (
      <p className="uppercase text-sm md:text-md text-lightRed">
      Stock: {device.stock}
  </p>
)}
          {/* Spare Parts, Recycled Date, Palette */}
          <div className="flex flex-col gap-1 mb-2">
            {device.device_options?.spare_parts && (
              <p className="uppercase text-sm md:text-md flex">
                Spare parts:{" "}
                {dayjs
                  .utc(device.device_options.spare_parts)
                  .format("DD/MM/YYYY")}
              </p>
            )}
            {device.device_options?.recycled_date && (
              <p className="uppercase text-sm md:text-md flex">
                Recycled date:{" "}
                {dayjs
                  .utc(device.device_options.recycled_date)
                  .format("DD/MM/YYYY")}
              </p>
            )}
            {device.palette > 0 && (
              <p className="uppercase text-sm md:text-md text-lightRed">
                Palette: {device.palette}
              </p>
            )}
          </div>
          {/* Comment */}
          {device.comment && (
            <p className="uppercase text-sm md:text-md">
              Comment: {device.comment}
            </p>
          )}
          {/* Register By */}
          <div>
            <p className="uppercase text-sm md:text-md">
              Last edited by:{" "}
              {device.user &&
                (device.user.name?.[0] || "") +
                  "." +
                  (device.user.last_name?.[0] || "")}
            </p>
          </div>
        </div>

        <div className="flex print-bottom justify-center items-center pt-8">
          <img
            src={Ritex}
            alt="Company Logo"
            width="100px"
            height="100px"
            className="mr-10 object-cover"
          />
          <a href="https://www.aritex.ch/">www.aritex.ch</a>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <AdminSidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading devices...</span>
        </div>
      </AdminSidebar>
    );
  }

  if (error) {
    return (
      <AdminSidebar>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error.message || "Failed to fetch devices. Please try again."}
        </div>
      </AdminSidebar>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-md">
        No devices found. Please check the device IDs in the URL.
      </div>
    );
  }

  return (
    <AdminSidebar>
      <div className="flex flex-col items-center justify-center bg-bgBlue py-6 md:py-8 px-4 sm:px-8 ">
        {/* Remove the old tab buttons (the map with setActiveTab(index)) */}

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row mb-4 space-y-4 md:space-y-0 md:space-x-4">
          {/* <button
            onClick={handlePrintSingle}
            className="bg-bluePrimary text-white rounded-md px-4 py-3 font-bold flex items-center w-full md:w-auto"
          >
            <PrinterIcon className="w-4 mr-2" />
            <span>Print Current</span>
          </button> */}

          <button
            onClick={handlePrintAll}
            className="bg-bluePrimary text-white rounded-md px-4 py-3 font-bold flex items-center w-full md:w-auto"
          >
            <PrinterIcon className="w-4 mr-2" />
            <span>Print All</span>
          </button>

          {/* <button
            onClick={handleDownloadSingle}
            className="border border-bluePrimary text-bluePrimary bg-white rounded-md px-4 py-3 font-bold flex items-center w-full md:w-auto"
          >
            <ArrowDownTrayIcon className="w-4 mr-2" />
            <span>Download Current</span>
          </button> */}

          <button
            onClick={handleDownloadAll}
            className="border border-bluePrimary text-bluePrimary bg-white rounded-md px-4 py-3 font-bold flex items-center w-full md:w-auto"
          >
            <ArrowDownTrayIcon className="w-4 mr-2" />
            <span>Download All</span>
          </button>

          <a
            href={`/devices/form?next_unique_id=${nextUniqueId}`}
            className="border border-bluePrimary text-bluePrimary bg-white rounded-md px-4 py-3 font-bold text-center"
          >
            Register Device +
          </a>
        </div>

        {/* Single Device View */}
        <div className="w-full flex justify-center">
          {devices.length > 0 && (
            <DeviceCard
              key={activeTab}
              device={devices[activeTab]}
              index={activeTab}
              isActive={true}
            />
          )}
        </div>
        {/* Hidden print-all container */}
        <div style={{ display: "none" }}>
          <div ref={(el) => (componentRefs.current["printAll"] = el)}>
            {devices.map((device, index) => (
              <DeviceCard
                key={index}
                device={device}
                index={index}
                isActive={true}
              />
            ))}
          </div>
        </div>
        <div className="flex justify-center my-4">
          <button
            onClick={() => setActiveTab((prev) => Math.max(0, prev - 1))}
            disabled={activeTab === 0}
            className="px-4 py-2 mx-2 border border-bluePrimary rounded-md disabled:opacity-40 disabled:cursor-not-allowed bg-gray-50 hover:bg-gray-100 transition-colors flex items-center"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <span className="px-4 py-2 mx-2 bg-gray-100 rounded-md font-medium flex items-center">
            <input
              type="number"
              min={1}
              max={devices.length}
              value={activeTab + 1}
              onChange={(e) => {
                let val = Number(e.target.value);
                if (isNaN(val)) return;
                val = Math.max(1, Math.min(devices.length, val));
                setActiveTab(val - 1);
              }}
              className="w-8 text-center bg-gray-100 outline-none  border-b-2 border-bluePrimary"
              style={{ MozAppearance: "textfield" }}
            />
            <span className="ml-1">of {devices.length}</span>
          </span>
          <button
            onClick={() =>
              setActiveTab((prev) => Math.min(devices.length - 1, prev + 1))
            }
            disabled={activeTab === devices.length - 1}
            className="px-4 py-2 mx-2 border border-bluePrimary rounded-md disabled:opacity-40 disabled:cursor-not-allowed bg-gray-50 hover:bg-gray-100 transition-colors flex items-center"
          >
            <ChevronRightIcon className="w-5 h-5 " />
          </button>
        </div>
      </div>
    </AdminSidebar>
  );
}

export default MultiDeviceDetails;
